import React, { useState, useEffect } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Phone, Calculator } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { SERVICES } from '../lib/services-data';
import { COMPANY_INFO } from '../lib/utils';
import axios from 'axios';
import { SEOHead } from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const SIMULATOR_SERVICE_MAP = { menage: 'entretien-menager', jardinage: 'espaces-verts' };
const FREQ_LABELS = { hebdomadaire: 'chaque semaine', 'bi-mensuel': 'toutes les 2 semaines', mensuel: '1 fois par mois', ponctuel: 'ponctuel' };

export default function DevisPage() {
  const [searchParams] = useSearchParams();
  const { state: routeState } = useLocation();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [form, setForm] = useState({
    nom: '', telephone: '', email: '', ville: '', code_postal: '',
    services: [], heures_estimees: '', frequence: '', message: '',
    agency: 'artois.ptitservices', consent_rgpd: false, honeypot: ''
  });
  const [errors, setErrors] = useState({});

  // Pre-fill from router state (simulator / city pages — keeps the URL clean, SEO friendly)
  // with URL params kept as a fallback for external / legacy links.
  useEffect(() => {
    const pick = (key) => (routeState && routeState[key] != null ? String(routeState[key]) : searchParams.get(key));
    const service = pick('service');
    const heures = pick('heures');
    const frequence = pick('frequence');
    const ville = pick('ville');
    const cp = pick('cp');
    const from = pick('from');
    if (!service && !heures && !frequence && !ville) return;

    const mappedService = service ? (SIMULATOR_SERVICE_MAP[service] || (SERVICES.some(s => s.id === service) ? service : null)) : null;
    setForm(prev => ({
      ...prev,
      services: mappedService ? [mappedService] : prev.services,
      heures_estimees: heures || prev.heures_estimees,
      frequence: frequence || prev.frequence,
      ville: ville || prev.ville,
      code_postal: cp || prev.code_postal,
    }));
    if (from === 'simulateur' && (heures || frequence)) {
      const label = SERVICES.find(s => s.id === mappedService)?.title;
      setPrefill({ label, heures, frequence: FREQ_LABELS[frequence] || frequence });
    }
  }, [searchParams, routeState]);

  const validate = () => {
    const e = {};
    if (!form.nom.trim()) e.nom = 'Le nom est obligatoire';
    if (!form.telephone.trim()) e.telephone = 'Le téléphone est obligatoire';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Email invalide';
    if (form.services.length === 0) e.services = 'Sélectionnez au moins un service';
    if (!form.consent_rgpd) e.consent_rgpd = 'Vous devez accepter la politique de confidentialité';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleServiceToggle = (serviceId) => {
    setForm(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(s => s !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/quote-requests`, form);
      if (response.data.success) {
        setSuccess(true);
        toast.success('Demande envoyée !', { description: response.data.message });
      }
    } catch (err) {
      toast.error('Erreur', { description: 'Une erreur est survenue. Veuillez réessayer ou nous appeler.' });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="py-20">
        <div className="max-w-lg mx-auto px-4 text-center">
          <div className="w-16 h-16 rounded-full bg-[hsl(var(--brand-mint))] flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-[hsl(var(--primary))]" />
          </div>
          <h1 className="font-['Space_Grotesk'] text-2xl font-semibold mb-3">Demande envoyée avec succès !</h1>
          <p className="text-muted-foreground mb-6">Nous vous recontacterons sous 24h ouvrées pour discuter de votre projet.</p>
          <Link to="/"><Button>Retour à l'accueil</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SEOHead
        title="Devis gratuit — services à la personne"
        description="Demandez votre devis gratuit et sans engagement pour nos services à la personne : ménage, jardinage, repassage, petit bricolage. Réponse sous 24h. Crédit d'impôt 50% avec avance immédiate."
        path="/devis"
      />
      {/* Hero */}
      <section className="bg-[linear-gradient(135deg,hsla(195,20%,97%,1)_0%,hsla(182,40%,94%,1)_55%,hsla(195,20%,97%,1)_100%)] py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground">Accueil</Link>
            <span>/</span>
            <span className="text-foreground font-medium">Demande de devis</span>
          </div>
          <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Devis gratuit</Badge>
          <h1 className="font-['Space_Grotesk'] text-3xl sm:text-4xl font-semibold tracking-tight mb-3">Demander un devis gratuit</h1>
          <p className="text-muted-foreground max-w-xl">Remplissez le formulaire ci-dessous et nous vous recontacterons sous 24h ouvrées. Devis gratuit et sans engagement.</p>
        </div>
      </section>

      {/* Form */}
      <section className="py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <motion.form onSubmit={handleSubmit} initial="hidden" animate="visible" variants={fadeUp} data-testid="quote-form">
                <Card className="p-6 sm:p-8 space-y-5">
                  {prefill && (
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-[hsl(var(--brand-mint))]/40 border border-[hsl(var(--primary))]/20 text-sm" data-testid="quote-prefill-banner">
                      <Calculator className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium">Pré-rempli depuis le simulateur</span>
                        <span className="text-muted-foreground"> — {prefill.label ? `${prefill.label}, ` : ''}{prefill.heures ? `${prefill.heures}h par intervention` : ''}{prefill.frequence ? `, ${prefill.frequence}` : ''}. Vous pouvez tout modifier.</span>
                      </div>
                    </div>
                  )}
                  {/* Honeypot */}
                  <input type="text" name="hp" value={form.honeypot} onChange={e => setForm(p => ({...p, honeypot: e.target.value}))} className="hidden" tabIndex={-1} autoComplete="off" />

                  {/* Agency */}
                  <div>
                    <Label>Agence *</Label>
                    <Select value={form.agency} onValueChange={v => setForm(p => ({...p, agency: v}))}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="artois.ptitservices">Agence Artois</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="nom">Nom complet *</Label>
                      <Input id="nom" value={form.nom} onChange={e => setForm(p => ({...p, nom: e.target.value}))} placeholder="Votre nom" className="mt-1" />
                      {errors.nom && <p className="text-xs text-destructive mt-1">{errors.nom}</p>}
                    </div>
                    <div>
                      <Label htmlFor="telephone">Téléphone *</Label>
                      <Input id="telephone" value={form.telephone} onChange={e => setForm(p => ({...p, telephone: e.target.value}))} placeholder="06 00 00 00 00" className="mt-1" />
                      {errors.telephone && <p className="text-xs text-destructive mt-1">{errors.telephone}</p>}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="votre@email.com" className="mt-1" />
                    {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ville">Ville</Label>
                      <Input id="ville" value={form.ville} onChange={e => setForm(p => ({...p, ville: e.target.value}))} placeholder="Votre ville" className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="code_postal">Code postal</Label>
                      <Input id="code_postal" value={form.code_postal} onChange={e => setForm(p => ({...p, code_postal: e.target.value}))} placeholder="62000" className="mt-1" />
                    </div>
                  </div>

                  {/* Services selection */}
                  <div>
                    <Label>Services souhaités *</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                      {SERVICES.map(service => (
                        <label key={service.id} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer text-sm transition-colors ${
                          form.services.includes(service.id) ? 'border-[hsl(var(--primary))] bg-[hsl(var(--brand-mint))]/20' : 'border-border hover:border-[hsl(var(--primary))]/30'
                        }`}>
                          <Checkbox checked={form.services.includes(service.id)} onCheckedChange={() => handleServiceToggle(service.id)} />
                          {service.title}
                        </label>
                      ))}
                    </div>
                    {errors.services && <p className="text-xs text-destructive mt-1">{errors.services}</p>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="heures">Heures estimées / semaine</Label>
                      <Input id="heures" type="number" value={form.heures_estimees} onChange={e => setForm(p => ({...p, heures_estimees: e.target.value}))} placeholder="3" className="mt-1" />
                    </div>
                    <div>
                      <Label>Fréquence souhaitée</Label>
                      <Select value={form.frequence} onValueChange={v => setForm(p => ({...p, frequence: v}))}>
                        <SelectTrigger className="mt-1"><SelectValue placeholder="Sélectionnez" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hebdomadaire">Chaque semaine</SelectItem>
                          <SelectItem value="bi-mensuel">Toutes les 2 semaines</SelectItem>
                          <SelectItem value="mensuel">1 fois par mois</SelectItem>
                          <SelectItem value="ponctuel">Ponctuel</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message">Message (optionnel)</Label>
                    <Textarea id="message" value={form.message} onChange={e => setForm(p => ({...p, message: e.target.value}))} placeholder="Décrivez vos besoins spécifiques..." className="mt-1" rows={4} />
                  </div>

                  <div>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <Checkbox checked={form.consent_rgpd} onCheckedChange={v => setForm(p => ({...p, consent_rgpd: v}))} className="mt-0.5" />
                      <span className="text-xs text-muted-foreground">J'accepte que mes données soient utilisées pour traiter ma demande. Conformité RGPD. *</span>
                    </label>
                    {errors.consent_rgpd && <p className="text-xs text-destructive mt-1">{errors.consent_rgpd}</p>}
                  </div>

                  <Button type="submit" disabled={loading} className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white" data-testid="quote-form-submit-button">
                    {loading ? 'Envoi en cours...' : 'Envoyer ma demande de devis'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Card>
              </motion.form>
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              <Card className="p-5 bg-[hsl(var(--brand-mint))]/20 border-0">
                <h3 className="font-['Space_Grotesk'] font-semibold text-sm mb-3">Réponse sous 24h</h3>
                <p className="text-xs text-muted-foreground">Notre équipe vous recontactera rapidement pour discuter de vos besoins et vous proposer un devis personnalisé.</p>
              </Card>
              <Card className="p-5 bg-[hsl(var(--brand-mint))]/20 border-0">
                <h3 className="font-['Space_Grotesk'] font-semibold text-sm mb-3">Tarifs transparents</h3>
                <p className="text-xs text-muted-foreground">Nous mettons un point d'honneur sur la transparence. Tous nos tarifs sont clairs et détaillés. Pas de surprise, le devis fait foi.</p>
              </Card>
              <Card className="p-5 bg-[hsl(var(--brand-mint))]/20 border-0">
                <h3 className="font-['Space_Grotesk'] font-semibold text-sm mb-3">Crédit d'impôt 50%</h3>
                <p className="text-xs text-muted-foreground">Tous nos services sont éligibles. Nous nous occupons de l'inscription à l'avance immédiate.</p>
              </Card>
              <Card className="p-5">
                <h3 className="font-['Space_Grotesk'] font-semibold text-sm mb-3">Appelez-nous</h3>
                <a href={`tel:${COMPANY_INFO.phoneRaw}`} className="flex items-center gap-2 text-sm text-[hsl(var(--primary))] font-medium">
                  <Phone className="h-4 w-4" />
                  {COMPANY_INFO.phone}
                </a>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
